import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Skeleton,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HistoryIcon from "@mui/icons-material/History";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import {
  getCanonicalLastRead,
  getRecentChapters,
  formatRelativeTime,
} from "../../bible/readingHistoryStorage";
import { getChapterVersePosition } from "../../bible/readingPositionStorage";
import {
  formatChapterChipLabel,
  formatContinueChapterTitle,
} from "../../bible/bookNames";
import { AuthContext } from "../../firebase/authContext";

const ContinueReadingCard = ({ onOpenChapter, edition }) => {
  const auth = useContext(AuthContext);
  const waitingForAuthoritative = Boolean(auth && !auth.readingReady);
  const [lastRead, setLastRead] = useState(() =>
    waitingForAuthoritative ? null : getCanonicalLastRead()
  );
  const [recentList, setRecentList] = useState(() =>
    waitingForAuthoritative ? [] : getRecentChapters()
  );
  const isEn = edition?.id === "en";

  const syncData = useCallback(() => {
    setLastRead(getCanonicalLastRead());
    setRecentList(getRecentChapters());
  }, []);

  useEffect(() => {
    if (waitingForAuthoritative) {
      setLastRead(null);
      setRecentList([]);
      return undefined;
    }
    syncData();
    window.addEventListener("bible_last_read_changed", syncData);
    window.addEventListener("pageshow", syncData);
    window.addEventListener("focus", syncData);
    return () => {
      window.removeEventListener("bible_last_read_changed", syncData);
      window.removeEventListener("pageshow", syncData);
      window.removeEventListener("focus", syncData);
    };
  }, [syncData, waitingForAuthoritative]);

  const defaultChapterId = "1-1";
  const savedVerse = lastRead?.chapterId
    ? getChapterVersePosition(lastRead.chapterId)
    : null;

  const handleContinue = () => {
    if (lastRead?.chapterId) {
      if (savedVerse && savedVerse > 1) {
        onOpenChapter(lastRead.chapterId, savedVerse);
      } else {
        onOpenChapter(lastRead.chapterId);
      }
    } else {
      onOpenChapter(defaultChapterId);
    }
  };

  const otherRecents = (recentList || [])
    .filter((r) => r.chapterId !== lastRead?.chapterId)
    .slice(0, 4);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
        overflow: "hidden",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)"
            : "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(255, 255, 255, 0.6) 100%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <BookmarkAddedIcon
              color="primary"
              sx={{ fontSize: { xs: 18, sm: 20 } }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "primary.main",
              }}
            >
              {isEn ? "Continue Reading" : "继续阅读"}
            </Typography>
          </Box>

          {!waitingForAuthoritative && lastRead?.timestamp && (
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(lastRead.timestamp, isEn)}
            </Typography>
          )}
        </Box>

        {waitingForAuthoritative ? (
          <Box data-testid="continue-reading-skeleton" aria-busy="true" aria-live="polite">
            <Skeleton variant="text" width="70%" height={36} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="45%" height={22} />
          </Box>
        ) : lastRead ? (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                lineHeight: 1.3,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                mb: 0.5,
              }}
            >
              {formatContinueChapterTitle(lastRead, isEn)}
              {savedVerse && savedVerse > 1
                ? isEn
                  ? ` · Verse ${savedVerse}`
                  : ` · 第 ${savedVerse} 节`
                : ""}
            </Typography>
            {lastRead.title && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {lastRead.title}
              </Typography>
            )}
          </Box>
        ) : (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: "text.primary",
                lineHeight: 1.3,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                mb: 0.5,
              }}
            >
              {isEn ? "Genesis 1 · The Creation" : "创世记 第1章 · 神的创造"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEn
                ? "Start your reading journey from the beginning"
                : "开始你的读经之旅，从起初探寻生命的奥秘"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action button & Recent Pills */}
      <Box>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleContinue}
          disabled={waitingForAuthoritative}
          startIcon={<PlayArrowIcon />}
          sx={{
            py: 1,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.95rem",
            boxShadow: "0 2px 8px rgba(25, 118, 210, 0.25)",
            "&:hover": {
              boxShadow: "0 4px 14px rgba(25, 118, 210, 0.35)",
            },
          }}
        >
          {waitingForAuthoritative
            ? isEn
              ? "Loading..."
              : "加载中..."
            : lastRead
            ? isEn
              ? "Continue Reading"
              : "继续阅读"
            : isEn
            ? "Start Reading (Gen 1)"
            : "开始阅读 (创世记 1)"}
        </Button>

        {!waitingForAuthoritative && otherRecents.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.75,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <HistoryIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {isEn ? "Recent History" : "最近浏览"}
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/history"
                size="small"
                variant="text"
                sx={{
                  fontSize: "0.72rem",
                  p: 0,
                  minWidth: "auto",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {isEn ? "View All" : "全部记录"}
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              {otherRecents.map((item) => (
                <Chip
                  key={item.chapterId}
                  size="small"
                  variant="outlined"
                  label={formatChapterChipLabel(item, isEn)}
                  onClick={() => onOpenChapter(item.chapterId)}
                  sx={{
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "action.hover",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ContinueReadingCard;
